/**
 * Customer-facing Support page — file a ticket and view your thread history.
 *
 * The KNWN4 team replies in-app from /super-admin/support; updates appear here
 * the next time you open the page (or sooner, since the list + open thread
 * poll on a short interval). There is no transactional email today — per
 * Eli's directive 2026-05-16, support@knwn4.com is an admin-panel-only flow.
 *
 * Page state model:
 *   - List view (default): all my tickets, newest activity first.
 *   - Thread view: dialog opens for one ticket, fetches messages, exposes
 *     a reply box. Closing the dialog returns to the list.
 *   - "New ticket" dialog: form with subject + category + priority + body.
 *
 * Polling: the list refetches every 30s, the open thread every 15s — so
 * staff replies surface without the user reloading.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Sparkles,
  LifeBuoy,
  Plus,
  Send,
  Loader2,
  AlertCircle,
  MessageSquare,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  useSupportTickets,
  useSupportTicket,
  useCreateTicket,
  useReplyToTicket,
  useMarkTicketRead,
  statusLabel,
  categoryLabel,
  priorityLabel,
  type SupportTicket,
  type SupportTicketCategory,
  type SupportTicketPriority,
  type SupportTicketStatus,
} from '@/features/support/use-support'

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------

export default function SupportPage() {
  const tickets = useSupportTickets()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [showNew, setShowNew] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const [prefillCategory, setPrefillCategory] = useState<SupportTicketCategory | null>(null)

  // Honor /support?new=<category> deep links so other surfaces (e.g. the
  // "Request it here" link in the AI message preview) can land users
  // directly on the new-ticket form with the right category preset. We
  // strip the param after consuming it so a back/forward navigation
  // doesn't re-open the dialog unexpectedly.
  useEffect(() => {
    const newParam = searchParams.get('new')
    if (!newParam) return
    const valid: readonly SupportTicketCategory[] = ['bug', 'billing', 'feature_request', 'how_to', 'other']
    const cat = (valid as readonly string[]).includes(newParam)
      ? (newParam as SupportTicketCategory)
      : 'other'
    setPrefillCategory(cat)
    setShowNew(true)
    const next = new URLSearchParams(searchParams)
    next.delete('new')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  return (
    <div className="mx-auto max-w-5xl space-y-5 p-4 sm:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <p className="eyebrow text-[var(--color-cobalt-bright)]">// SUPPORT</p>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-[var(--color-silver-bright)]">
            Help &amp; Support
          </h1>
          <p className="mt-1 text-xs sm:text-sm text-[var(--color-silver)]">
            File a ticket and the KNWN4 team will reply right here. Typical response within one business day.
          </p>
        </div>
        <Button onClick={() => setShowNew(true)} className="self-start sm:self-auto shrink-0">
          <Plus className="mr-1 h-4 w-4" />
          New ticket
        </Button>
      </div>

      <TicketsList
        loading={tickets.isLoading}
        error={tickets.error instanceof Error ? tickets.error.message : null}
        tickets={tickets.data ?? []}
        onOpen={(id) => setSelectedId(id)}
        onCreate={() => setShowNew(true)}
      />

      <NewTicketDialog
        open={showNew}
        onOpenChange={(v) => {
          setShowNew(v)
          if (!v) setPrefillCategory(null)
        }}
        initialCategory={prefillCategory}
      />
      <ThreadDialog
        ticketId={selectedId}
        onClose={() => setSelectedId(null)}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tickets list
// ---------------------------------------------------------------------------

function TicketsList({
  loading,
  error,
  tickets,
  onOpen,
  onCreate,
}: {
  readonly loading: boolean
  readonly error: string | null
  readonly tickets: readonly SupportTicket[]
  readonly onOpen: (id: string) => void
  readonly onCreate: () => void
}) {
  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading tickets…
        </CardContent>
      </Card>
    )
  }
  if (error) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-6 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" />
          Failed to load tickets: {error}
        </CardContent>
      </Card>
    )
  }
  if (tickets.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
          <LifeBuoy className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm font-medium">No support tickets yet</p>
          <p className="max-w-md text-xs text-muted-foreground">
            Need help? Hit a bug? Have a feature idea? Open a ticket and the KNWN4 team will reply right here.
          </p>
          <Button size="sm" onClick={onCreate}>
            <Plus className="mr-1 h-3.5 w-3.5" />
            Open first ticket
          </Button>
        </CardContent>
      </Card>
    )
  }
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Your tickets</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <ul className="divide-y divide-border">
          {tickets.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                onClick={() => onOpen(t.id)}
                className="flex w-full items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-muted/40"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <p className="truncate text-sm font-medium">{t.subject}</p>
                    {t.user_unread_count > 0 && (
                      <Badge className="shrink-0 bg-[var(--color-cobalt-bright)] text-[10px] text-white">
                        {t.user_unread_count} new
                      </Badge>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <StatusBadge status={t.status} />
                    <span>·</span>
                    <span>{categoryLabel(t.category)}</span>
                    {t.priority !== 'normal' && (
                      <>
                        <span>·</span>
                        <span className={priorityClass(t.priority)}>{priorityLabel(t.priority)}</span>
                      </>
                    )}
                    <span>·</span>
                    <span>Last activity {formatRelative(t.last_message_at)}</span>
                  </div>
                </div>
                <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// New-ticket dialog
// ---------------------------------------------------------------------------

function NewTicketDialog({
  open,
  onOpenChange,
  initialCategory,
}: {
  readonly open: boolean
  readonly onOpenChange: (v: boolean) => void
  readonly initialCategory?: SupportTicketCategory | null
}) {
  const create = useCreateTicket()
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [category, setCategory] = useState<SupportTicketCategory>('other')
  const [priority, setPriority] = useState<SupportTicketPriority>('normal')
  const [error, setError] = useState<string | null>(null)

  // Reset form when dialog closes; honor an initial category when it opens.
  useEffect(() => {
    if (!open) {
      setSubject('')
      setBody('')
      setCategory('other')
      setPriority('normal')
      setError(null)
    } else if (initialCategory) {
      setCategory(initialCategory)
    }
  }, [open, initialCategory])

  async function handleSubmit() {
    setError(null)
    const s = subject.trim()
    const b = body.trim()
    if (!s) return setError('Subject is required.')
    if (!b) return setError('Please describe the issue.')
    try {
      await create.mutateAsync({ subject: s, body: b, category, priority })
      onOpenChange(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create ticket.')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-full max-w-lg max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg pr-6">
            <Sparkles className="h-4 w-4 text-[var(--color-cobalt-bright)]" />
            New support ticket
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            We'll reply right here in the support page. You'll see a badge on the page when there's a new reply.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ticket-subject">Subject</Label>
            <Input
              id="ticket-subject"
              placeholder="One-line summary, e.g. 'Connection request failed for X account'"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label>Category</Label>
              <Select value={category} onValueChange={(v) => setCategory(v as SupportTicketCategory)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bug">Bug — something's broken</SelectItem>
                  <SelectItem value="billing">Billing or subscription</SelectItem>
                  <SelectItem value="feature_request">Feature request</SelectItem>
                  <SelectItem value="how_to">How-to / usage question</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Priority</Label>
              <Select value={priority} onValueChange={(v) => setPriority(v as SupportTicketPriority)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low — when you get to it</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High — blocking my work</SelectItem>
                  <SelectItem value="urgent">Urgent — outage / data loss</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ticket-body">Describe the issue</Label>
            <textarea
              id="ticket-body"
              className="min-h-[140px] max-h-[300px] resize-y rounded-md border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder="What were you trying to do? What happened instead? Include error messages or steps to reproduce if you can."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={8000}
            />
            <p className="text-[10px] text-muted-foreground">{body.length}/8000 characters</p>
          </div>

          {error && (
            <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={create.isPending}>
            {create.isPending ? (
              <><Loader2 className="mr-1 h-4 w-4 animate-spin" />Submitting…</>
            ) : (
              <><Send className="mr-1 h-4 w-4" />Submit ticket</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Thread dialog — read messages + reply
// ---------------------------------------------------------------------------

function ThreadDialog({
  ticketId,
  onClose,
}: {
  readonly ticketId: string | null
  readonly onClose: () => void
}) {
  const detail = useSupportTicket(ticketId)
  const reply = useReplyToTicket()
  const markRead = useMarkTicketRead()
  const [draft, setDraft] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)

  // When the dialog opens and there are unread staff replies, ack them so the
  // badge clears. Tracked via a ref so we mark-read exactly once per ticket
  // open, regardless of detail polling re-renders.
  const lastAckedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!ticketId) {
      lastAckedRef.current = null
      return
    }
    if (
      detail.data?.ticket
      && detail.data.ticket.user_unread_count > 0
      && lastAckedRef.current !== detail.data.ticket.id
    ) {
      lastAckedRef.current = detail.data.ticket.id
      markRead.mutate(ticketId)
    }
  }, [ticketId, detail.data, markRead])

  // Auto-scroll to bottom when messages change or reply is pending.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [detail.data?.messages.length, reply.isPending])

  async function handleSend() {
    if (!ticketId) return
    const b = draft.trim()
    if (!b) return
    setError(null)
    try {
      await reply.mutateAsync({ ticketId, body: b })
      setDraft('')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send reply.')
    }
  }

  const ticket = detail.data?.ticket
  const messages = detail.data?.messages ?? []
  const isClosed = ticket?.status === 'closed'

  return (
    <Dialog open={Boolean(ticketId)} onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-full max-w-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden p-4 sm:p-6">
        <DialogHeader className="pr-8">
          <div className="min-w-0">
            <DialogTitle className="truncate text-base sm:text-lg pr-2">{ticket?.subject ?? 'Ticket'}</DialogTitle>
            {ticket && (
              <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                <StatusBadge status={ticket.status} />
                <span className="hidden sm:inline">·</span>
                <span>{categoryLabel(ticket.category)}</span>
                {ticket.priority !== 'normal' && (
                  <>
                    <span className="hidden sm:inline">·</span>
                    <span className={priorityClass(ticket.priority)}>{priorityLabel(ticket.priority)}</span>
                  </>
                )}
                <span className="hidden sm:inline">·</span>
                <span>Opened {formatRelative(ticket.created_at)}</span>
              </div>
            )}
          </div>
        </DialogHeader>

        {detail.isLoading && (
          <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading thread…
          </div>
        )}

        {detail.error && (
          <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{detail.error instanceof Error ? detail.error.message : 'Failed to load'}</span>
          </div>
        )}

        {ticket && (
          <>
            <ScrollArea className="min-h-0 flex-1 rounded-md border bg-muted/10 p-3" ref={scrollRef as never}>
              <div ref={scrollRef} className="flex flex-col gap-3 pr-2">
                {messages.map((m) => (
                  <MessageBubble key={m.id} message={m} />
                ))}
                {messages.length === 0 && (
                  <p className="text-center text-xs italic text-muted-foreground">No messages yet.</p>
                )}
              </div>
            </ScrollArea>

            <Separator className="my-2" />

            {isClosed ? (
              <div className="rounded-md border bg-muted/20 p-3 text-xs text-muted-foreground">
                This ticket is closed. Open a new ticket if you need more help.
              </div>
            ) : (
              <div className="flex gap-2">
                <textarea
                  className="min-h-[60px] max-h-[140px] flex-1 resize-y rounded-md border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                  placeholder="Reply to the team…"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault()
                      void handleSend()
                    }
                  }}
                  disabled={reply.isPending}
                  maxLength={8000}
                />
                <Button onClick={handleSend} disabled={!draft.trim() || reply.isPending} className="self-end">
                  {reply.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Bubbles + status badge + helpers
// ---------------------------------------------------------------------------

function MessageBubble({ message }: { readonly message: { sender_type: 'user' | 'staff'; body: string; created_at: string } }) {
  const isStaff = message.sender_type === 'staff'
  return (
    <div className={`flex ${isStaff ? 'justify-start' : 'justify-end'}`}>
      <div
        className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
          isStaff
            ? 'rounded-tl-sm bg-muted text-foreground'
            : 'rounded-tr-sm bg-[var(--color-cobalt-bright)] text-white'
        }`}
      >
        <div className="text-[10px] opacity-70 mb-1">
          {isStaff ? 'KNWN4 Team' : 'You'} · {formatRelative(message.created_at)}
        </div>
        {message.body}
      </div>
    </div>
  )
}

function StatusBadge({ status }: { readonly status: SupportTicketStatus }) {
  const tone = useMemo(() => {
    switch (status) {
      case 'open': return 'border-blue-500/30 bg-blue-500/10 text-blue-300'
      case 'in_progress': return 'border-yellow-500/30 bg-yellow-500/10 text-yellow-300'
      case 'waiting_on_user': return 'border-orange-500/30 bg-orange-500/10 text-orange-300'
      case 'resolved': return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
      case 'closed': return 'border-gray-500/30 bg-gray-500/10 text-gray-300'
    }
  }, [status])
  return (
    <Badge variant="outline" className={`text-[10px] ${tone}`}>
      {statusLabel(status)}
    </Badge>
  )
}

function priorityClass(p: SupportTicketPriority): string {
  switch (p) {
    case 'low': return 'text-muted-foreground'
    case 'normal': return ''
    case 'high': return 'text-orange-300'
    case 'urgent': return 'text-red-300 font-medium'
  }
}

function formatRelative(iso: string): string {
  const t = Date.parse(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z')
  if (Number.isNaN(t)) return iso
  const diffMs = Date.now() - t
  const diffMin = Math.floor(diffMs / 60_000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay < 7) return `${diffDay}d ago`
  return new Date(t).toLocaleDateString()
}
